import React, { useState, useEffect, useContext } from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import { useNavigate, Link } from 'react-router-dom';
import enslogo from '../assets/images/logos/ens-logo-light.png';
import { ShowIf, useSecurity } from '../components/Security/security';
import { CONTACT_US_URL } from '../utils/constants';
import { isEORIUser, isITUser } from '../utils/roles';
import MaintenanceBanner, { MaintenanceBannerAdminModal } from '../components/MaintenanceBanner';
import { StoreContext } from '../components/Store';
import ThreeSixtyPayLogo from '../assets/images/360pay-grey-logo.png';
import ThreeSixtyPayENSLogo from '../assets/images/logos/ens-logo-dark.png';
import { SSOContext } from '../components/SSO/SSO';
import MoreAppsButton from '../components/MoreAppsButton/MoreAppsButton';
import LoadingSpinner from '../components/LoadingSpinner';
import PersonIcon from '@mui/icons-material/Person';
import Popover from '@mui/material/Popover';
import dayjs from 'dayjs';

const AlternateLogo = () => {
  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <img
        src={ThreeSixtyPayLogo}
        alt="360pay Logo"
        style={{
          height: '40px',
          width: 'auto',
          marginTop: 22,
          marginBottom: 22,
        }}
      />
      <span style={{ display: 'block', color: '#000', paddingLeft: 20, paddingRight: 20 }}>Powered By</span>
      <img
        src={ThreeSixtyPayENSLogo}
        alt="CABIE ENS Logo"
        style={{
          height: '50px',
          width: 'auto',
          marginTop: 17,
          marginBottom: 17,
        }}
      />
    </div>
  );
};

const DefaultLogo = () => {
  return (
    <img
      src={enslogo}
      alt="ENS Logo"
      style={{ height: '90px', width: 'auto', marginTop: '10px', marginBottom: '10px' }}
    />
  );
};

const Navigation = ({ handleLogOut, handleNavigate, siteConfig, onOpenMaintenanceBanner }) => {
  const navigate = useNavigate();

  const [userInfoEl, setUserInfoEl] = useState(null);
  const [adminMenuAnchor, setAdminMenuAnchor] = useState(null);

  const handlePopoverOpen = (event) => {
    setUserInfoEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setUserInfoEl(null);
  };

  const getSessionStorageValue = (value) => {
    if (value === 'null' || value === 'undefined' || !value) return '';
    return value;
  };

  const showUserInfo = Boolean(userInfoEl);

  const userFullName = getSessionStorageValue(sessionStorage.getItem('fullName') || '');
  const userCompanyName = getSessionStorageValue(sessionStorage.getItem('company') || '');
  const userDepartment = getSessionStorageValue(sessionStorage.getItem('department') || '');
  const userRole = getSessionStorageValue(sessionStorage.getItem('role') || '');
  const userLastLogin = getSessionStorageValue(sessionStorage.getItem('lastLoggedIn') || '');

  const navButtonStyle = {
    my: 2,
    mx: 2,
    color: siteConfig.navButtonColor || '#fff',
    display: 'block',
    fontSize: '17px',
    textTransform: 'none',
    fontWeight: '100',
    fontFamily: 'Roboto',
  };

  const userIconStyle = {
    display: 'flex',
    alignItems: 'center',
    mx: 3,
  };

  const UserInfoItem = ({ label, value }) => {
    if (!value) return null;

    return (
      <Box sx={{ my: 1 }}>
        <Typography variant="body2">
          <strong>{label}:</strong> {value}
        </Typography>
      </Box>
    );
  };

  return (
    <>
      <ShowIf isLoggedIn>
        <Button key="Create ENS" onClick={() => navigate('/EnsDeclarationSelectCompany')} sx={navButtonStyle}>
          Request GB ENS
        </Button>

        <Button key="Dashboard" onClick={() => navigate('/Dashboard')} sx={navButtonStyle}>
          Dashboard
        </Button>

        <Button key="KB" onClick={() => window.open(process.env.REACT_APP_HUBSPOT_URL, '_blank')} sx={navButtonStyle}>
          Knowledge Base
        </Button>

        <Button key="contact us" onClick={() => window.open(`${CONTACT_US_URL}`, '_blank')} sx={navButtonStyle}>
          Contact Us
        </Button>

        {isITUser() && onOpenMaintenanceBanner && (
          <>
            <Button
              key="Admin"
              onClick={(e) => setAdminMenuAnchor(e.currentTarget)}
              sx={navButtonStyle}
              aria-controls={adminMenuAnchor ? 'admin-menu' : undefined}
              aria-haspopup="true"
            >
              Admin
            </Button>
            <Menu
              id="admin-menu"
              anchorEl={adminMenuAnchor}
              open={Boolean(adminMenuAnchor)}
              onClose={() => setAdminMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
              <MenuItem
                onClick={() => {
                  setAdminMenuAnchor(null);
                  onOpenMaintenanceBanner();
                }}
              >
                Maintenance banner
              </MenuItem>
            </Menu>
          </>
        )}

        <Button key="Status" onClick={() => handleNavigate()} sx={navButtonStyle}>
          Settings
        </Button>

        <Button key="Logout" onClick={handleLogOut} sx={navButtonStyle}>
          Log Out
        </Button>

        <Box sx={userIconStyle}>
          <Box
            aria-owns={showUserInfo ? 'user-info-popover' : undefined}
            aria-haspopup="true"
            onMouseEnter={handlePopoverOpen}
            onMouseLeave={handlePopoverClose}
          >
            <PersonIcon sx={{ color: siteConfig.navButtonColor || '#fff', fontSize: 30 }} />
          </Box>

          <Popover
            id="user-info-popover"
            sx={{ pointerEvents: 'none' }}
            open={showUserInfo}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            onClose={handlePopoverClose}
            onMouseLeave={handlePopoverClose}
            anchorEl={userInfoEl}
          >
            <Box sx={{ p: 2 }}>
              <Box>
                <UserInfoItem label="Name" value={userFullName} />
                <UserInfoItem label="Company" value={userCompanyName} />
                {userDepartment !== 'Haulier' && <UserInfoItem label="Department" value={userDepartment} />}
                <UserInfoItem label="Role" value={userRole} />
                {userLastLogin && dayjs(userLastLogin).isValid() && (
                  <UserInfoItem label="Last Login" value={dayjs(userLastLogin).format('DD/MM/YYYY HH:mm')} />
                )}
              </Box>
            </Box>
          </Popover>
        </Box>
      </ShowIf>

      <ShowIf isLoggedIn={false}>
        <Button key="Login" onClick={() => navigate('/login')} sx={{ ...navButtonStyle, fontSize: 20, mx: 0 }}>
          Login
        </Button>
      </ShowIf>
    </>
  );
};

const Header = () => {
  const store = useContext(StoreContext);
  const sso = useContext(SSOContext);
  const siteConfig = store.wlpConfig[0];

  const [anchorElUser, setAnchorElUser] = useState(null);
  const [maintenanceBannerModalOpen, setMaintenanceBannerModalOpen] = useState(false);
  const navigate = useNavigate();
  const { dispatch, isLoggedIn } = useSecurity();
  const [isLoadingSSO] = store.isLoadingSSO;

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };
  const handleLogOut = async () => {
    if (sessionStorage.getItem('sso') === '1') {
      sso.handleSSOLogout();
    }
    sessionStorage.removeItem('token');
    sessionStorage.removeItem(process.env.REACT_APP_API + 'userdata');
    dispatch({ type: 'logout' });
    navigate('/');
  };
  const handleNavigate = () => {
    if (isEORIUser()) {
      navigate('/SelectCustomer');
    } else {
      navigate('/CustomerSettings');
    }
  };

  const showMoreApps = sessionStorage.getItem('sso') === '1';

  return (
    <>
      <AppBar
        position="static"
        sx={{ bgcolor: siteConfig.headerBgColor || '#1B4F96', color: siteConfig.headerTextColor || '#24709a' }}
        className="header-component"
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters>
            {showMoreApps && <MoreAppsButton />}
            <Box
              component={Link}
              to={isLoggedIn ? '/dashboard' : '/'}
              sx={{
                mr: 2,
                display: { md: 'flex' },
                textDecoration: 'none',
              }}
            >
              {siteConfig.wlpName === '360PAY' ? <AlternateLogo /> : <DefaultLogo />}
            </Box>

            {/* For wide screens */}

            <Box
              sx={{
                flexGrow: 1,
                display: { xs: 'none', md: 'flex', mr: '3' },
                marginTop: '4px',
                justifyContent: 'flex-end',
              }}
            >
              {isLoadingSSO ? (
                <LoadingSpinner />
              ) : (
                <Navigation
                  handleLogOut={handleLogOut}
                  handleNavigate={handleNavigate}
                  siteConfig={siteConfig}
                  onOpenMaintenanceBanner={() => setMaintenanceBannerModalOpen(true)}
                />
              )}
            </Box>

            <Box sx={{ flexGrow: 0 }}>
              <Menu
                sx={{ mt: '45px' }}
                id="menu-appbar"
                anchorEl={anchorElUser}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
              >
                <MenuItem key="Register" onClick={() => navigate('/register')}>
                  <Typography sx={{ textAlign: 'center', color: '#00AEEF' }}>Register</Typography>
                </MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      <MaintenanceBanner />

      <MaintenanceBannerAdminModal
        open={maintenanceBannerModalOpen}
        onClose={() => setMaintenanceBannerModalOpen(false)}
      />
    </>
  );
};

export default Header;
